import React, {FC, useCallback, useMemo} from 'react';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelTypesEnum} from '../../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {InboxesFiltersEnum} from '../../../../../models/url/models/inboxesUrlModel';
import {
  SettingsInboxesResourcePagesEnum,
  SettingsInboxesTabPagesEnum,
} from '../../../../../models/url/models/settingsInboxesUrlModel';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../../models/url/settingsUrlHelpers';
import {IntegrationIconsEnum} from '../../../../common/integrationIcon';
import {
  CellsEmptySetupActionsProps,
  StyledCellsEmptySetupActionsContentDiv,
  StyledCellsEmptySetupActionsSubTitleDiv,
  StyledCellsEmptySetupActionsTitleDiv,
  StyledCellsEmptySetupActionsWrapperDiv,
} from './cellsEmptyCommon';
import {CellsEmptySetupActionsButton} from './cellsEmptySetupActionsButton';
import {CellsEmptySetupActionsDismissButton} from './cellsEmptySetupActionsDismissButton';
import {CellsEmptySetupActionsLink} from './cellsEmptySetupActionsLink';

/*
 * Props.
 */

interface CellsEmptyTeammateSetupActionsProps extends CellsEmptySetupActionsProps {
  shouldShowAddTeamInbox: boolean;
  teamId?: number;
  selectedFilter?: InboxesFiltersEnum;
}

/*
 * Component.
 */

export const CellsEmptyTeammateSetupActions: FC<CellsEmptyTeammateSetupActionsProps> = (props) => {
  const newChannelCreationPath = useCallback(
    (channelType: ChannelTypesEnum) => {
      if (!props.namespace) {
        return '';
      }

      return buildSettingsPath({
        namespace: props.namespace,
        rootPage: SettingsRootPagesEnum.CHANNELS,
        resourcePage: SettingsResourcePagesEnum.NEW,
        channelType,
      });
    },
    [props.namespace],
  );

  const newInboxCreationPath = useMemo(() => {
    if (!props.namespace) {
      return '';
    }

    return buildSettingsPath({
      namespace: props.namespace,
      rootPage: SettingsRootPagesEnum.INBOXES,
      resourcePage: SettingsInboxesResourcePagesEnum.NEW,
      tabPage: SettingsInboxesTabPagesEnum.INBOXES,
    });
  }, [props.namespace]);

  return (
    <StyledCellsEmptySetupActionsWrapperDiv>
      <StyledCellsEmptySetupActionsContentDiv>
        <StyledCellsEmptySetupActionsTitleDiv>
          <Trans id="cells-empty-cta-teammate-title">Add your work email</Trans>
        </StyledCellsEmptySetupActionsTitleDiv>
        <StyledCellsEmptySetupActionsSubTitleDiv>
          <Trans id="cells-empty-cta-teammate-subtitle">
            Once you&apos;ve connected inboxes, all your messages will appear here.
          </Trans>
        </StyledCellsEmptySetupActionsSubTitleDiv>
        {props.namespace && (
          <>
            <CellsEmptySetupActionsButton
              buttonVariant={ButtonVariantsEnum.PRIMARY}
              interactionId="cells-empty-cta-teammate-googlebutton"
              title={<Trans id="cells-empty-cta-teammate-googlebutton">Add with Google</Trans>}
              integrationIcon={IntegrationIconsEnum.GOOGLE}
              onClick={props.navigate(newChannelCreationPath(ChannelTypesEnum.GMAIL))}
            />
            <CellsEmptySetupActionsButton
              buttonVariant={ButtonVariantsEnum.PRIMARY}
              interactionId="cells-empty-cta-teammate-microsoftbutton"
              title={<Trans id="cells-empty-cta-teammate-microsoftbutton">Add with Microsoft</Trans>}
              integrationIcon={IntegrationIconsEnum.MICROSOFT}
              onClick={props.navigate(newChannelCreationPath(ChannelTypesEnum.OFFICE365))}
            />
            <CellsEmptySetupActionsButton
              buttonVariant={ButtonVariantsEnum.SECONDARY}
              interactionId="cells-empty-cta-teammate-otherbutton"
              title={<Trans id="cells-empty-cta-teammate-otherbutton">Other email client</Trans>}
              onClick={props.navigate(newChannelCreationPath(ChannelTypesEnum.SMTP))}
            />
          </>
        )}
        {props.shouldShowAddTeamInbox && props.teamId && props.selectedFilter && (
          <CellsEmptySetupActionsLink
            interactionId="cells-empty-cta-teammate-teaminboxlink"
            title={<Trans id="cells-empty-cta-teammate-teaminboxlink">Add a shared inbox</Trans>}
            onClick={props.navigate(newInboxCreationPath)}
          />
        )}
      </StyledCellsEmptySetupActionsContentDiv>
      <CellsEmptySetupActionsDismissButton
        interactionId="cells-empty-cta-teammate-dismissbutton"
        onClick={props.onDismissClick}
      />
    </StyledCellsEmptySetupActionsWrapperDiv>
  );
};
